/**
 * settingsError -- Format a settings API error envelope into a single toast string.
 *
 * The streaming/WSD settings endpoints return validation failures as
 * `{ ok: false, message, errors }`, where `errors` is the Zod
 * `flatten().fieldErrors` map (field key -> array of issue strings). The save
 * hooks previously toasted only `message` ("Validation error"), discarding the
 * per-field detail the user needs to know WHICH field to fix.
 *
 * `formatSettingsError` turns that envelope into one human-readable line, e.g.
 *   "Validation error - External Link (Plex): Must be a valid URL"
 * mapping raw field keys to the labels the user actually sees in the form.
 * Unknown keys fall back to the raw key.
 */

/**
 * Raw field key -> visible form label, across every WSD/streaming settings
 * surface (streaming servers, pause rules, notifications, transcode purge,
 * analytics, cover art, media requests). Field keys are globally unique except
 * `enabled` (a boolean shared by every form, which validates generically).
 */
export const WSD_SETTINGS_FIELD_LABELS: Record<string, string> = {
  // Streaming dashboard (useWSDSettings)
  enabled: "Enabled",
  pollInterval: "Poll Interval",
  geoIpEnabled: "GeoIP Lookups",
  embyEnabled: "Emby",
  embyUrl: "Server URL (Emby)",
  embyApiKey: "API Key (Emby)",
  embyExternalUrl: "External Link (Emby)",
  jellyfinEnabled: "Jellyfin",
  jellyfinUrl: "Server URL (Jellyfin)",
  jellyfinApiKey: "API Key (Jellyfin)",
  jellyfinExternalUrl: "External Link (Jellyfin)",
  plexEnabled: "Plex",
  plexUrl: "Server URL (Plex)",
  plexApiKey: "X-Plex-Token (Plex)",
  plexExternalUrl: "External Link (Plex)",
  plexClientIdentifier: "Plex Client Identifier",
  dispatcharrBaseUrl: "Base URL (Dispatcharr)",
  dispatcharrApiKey: "API Key (Dispatcharr)",

  // Pause rules (usePauseRuleSettings)
  moviesWarningSeconds: "Movies Warning Threshold",
  moviesStopSeconds: "Movies Stop Threshold",
  tvWarningSeconds: "TV Warning Threshold",
  tvStopSeconds: "TV Stop Threshold",
  livetvWarningSeconds: "Live TV Warning Threshold",
  livetvStopSeconds: "Live TV Stop Threshold",
  noticeTemplate: "Warning Notice Template",
  stopMessageHeader: "Stop Message Header",
  stopMessageText: "Stop Message Text",

  // Notifications (useNotificationSettings)
  defaultSubject: "Default Subject",
  defaultTemplate: "Default Template",

  // Transcode purge (useTranscodePurgeSettings)
  purgePath: "Purge Path",
  logsPath: "Logs Path",
  moviesMaxAgeMinutes: "Movies Max Age",
  tvMaxAgeMinutes: "TV Max Age",
  livetvMaxAgeMinutes: "Live TV Max Age",
  minAgeMinutes: "Min Age Before Delete",
  maxDeletesPerRun: "Max Deletes Per Run",
  activeGraceMinutes: "Active Grace",
  segmentRetentionMinutes: "Segment Retention",

  // Analytics (useAnalyticsSettings)
  completeThreshold: "Completed Threshold",
  abandonThreshold: "Abandoned Threshold",
  minWatchTimeSecs: "Min Watch Time",
  missingSessionThreshold: "Missing Cycles",
  excludedUsers: "Excluded Users",
  timeRangeDays: "Analytics Time Range",
  retentionDays: "Data Retention",

  // Cover art (useCoverArtSettings)
  tmdbEnabled: "TMDB Cover Art",
  tmdbApiKey: "TMDB API Key",
  tvdbEnabled: "TheTVDB Cover Art",
  tvdbApiKey: "TheTVDB API Key",
  movieProvider: "Movie Libraries",
  seriesProvider: "Series Libraries",

  // Media requests (useMediaRequestsSettings)
  defaultView: "Default View",
  inLibraryPageSize: "In-Library Page Size",
  seerr: "Seerr",
  ombi: "Ombi",
};

/** Map a raw field key to its visible label, falling back to the raw key. */
export function settingsFieldLabel(key: string): string {
  return WSD_SETTINGS_FIELD_LABELS[key] ?? key;
}

type SettingsErrorEnvelope = {
  message?: unknown;
  errors?: unknown;
  formErrors?: unknown;
};

/**
 * Format a settings API error response into a single toast string: the
 * envelope message, then each failing field as `<Label>: <first issue>`, then
 * any object-level (`formErrors`) messages, which are already human sentences
 * and need no label.
 *
 * @param data     the parsed response body (`{ message?, errors?, formErrors? }`);
 *                 untyped because it comes straight from `res.json()`.
 * @param fallback message to use when the envelope carries none.
 */
export function formatSettingsError(data: unknown, fallback: string): string {
  const envelope = (data ?? {}) as SettingsErrorEnvelope;

  const base =
    typeof envelope.message === "string" && envelope.message.trim().length > 0
      ? envelope.message
      : fallback;

  const detailParts: string[] = [];

  const errors = envelope.errors;
  if (typeof errors === "object" && errors !== null) {
    for (const [key, issues] of Object.entries(errors as Record<string, unknown>)) {
      const firstIssue =
        Array.isArray(issues) &&
        typeof issues[0] === "string" &&
        issues[0].trim().length > 0
          ? issues[0]
          : null;
      if (firstIssue) {
        detailParts.push(`${settingsFieldLabel(key)}: ${firstIssue}`);
      }
    }
  }

  // Object-level (`.refine`) failures have no field path; they arrive as
  // `formErrors` and are already complete sentences — append after the fields.
  if (Array.isArray(envelope.formErrors)) {
    for (const issue of envelope.formErrors) {
      if (typeof issue === "string" && issue.trim().length > 0) {
        detailParts.push(issue);
      }
    }
  }

  if (detailParts.length === 0) {
    return base;
  }

  return `${base} - ${detailParts.join("; ")}`;
}
